import { Plugin, PluginKey } from 'prosemirror-state';
import { EditorView } from 'prosemirror-view';

export const slideshowPluginKey = new PluginKey('slideshow');

/**
 * Finds the slide and its parent slideshow for the current selection.
 * @param editorView The editor view.
 * @returns The positions and index of the slide and its parent slideshow.
 */
function findSlideAndParentSlideshow(editorView: EditorView): { slidePos: number; slideshowPos: number; slideIndex: number } {
  const $from = editorView.state.selection.$from;

  // Determine the slide and slideshow depths
  let slideDepth = -1;
  let slideshowDepth = -1;
  for (let depth = $from.depth; depth >= 0; depth -= 1) {
    const name = $from.node(depth).type.name;
    if (slideDepth < 0 && name === 'madcapslide') {
      slideDepth = depth;
    }
    if (slideshowDepth < 0 && name === 'madcapslideshow') {
      slideshowDepth = depth;
    }
  }

  if (slideDepth < 0 || slideshowDepth < 0) {
    return;
  }

  // Ensure the slideshow is an ancestor of the slide (i.e. appears at a shallower depth)
  if (slideshowDepth >= slideDepth) {
    return;
  }

  // Grab the slideshow node
  const slidePos = $from.before(slideDepth);
  const slideshowPos = $from.before(slideshowDepth);
  const slideshowNode = editorView.state.doc.nodeAt(slideshowPos);
  if (!slideshowNode) {
    return;
  }

  // Determine the slide index by iterating the children
  let slideIndex = 0;
  let offset = 0;
  for (let i = 0; i < slideshowNode.childCount; i += 1) {
    const childStart = slideshowPos + 1 + offset;
    if (childStart === slidePos) {
      slideIndex = i;
      break;
    }
    offset += slideshowNode.child(i).nodeSize;
  }

  return { slidePos, slideshowPos, slideIndex };
}

/**
 * Slideshow plugin to detect when the selection enters a slide and notify the slideshow node view.
 * This plugin assumes MadCap:slide is a direct child of MadCap:slideshow.
 * @returns The ProseMirror plugin.
 */
export function slideshowPlugin(): Plugin {
  return new Plugin({
    key: slideshowPluginKey,

    view() {
      let lastKey = '';
      return {
        update(editorView, prevState) {
          // If the selection hasn't changed, do nothing
          if (editorView.state.selection.eq(prevState.selection)) {
            return;
          }

          // Find the slide info for the current selection
          const info = findSlideAndParentSlideshow(editorView);
          if (!info) {
            return;
          }

          // Cheap way to avoid duplicate events for the same slide
          const key = `${info.slideshowPos}:${info.slideIndex}`;
          if (key === lastKey) {
            return;
          }
          lastKey = key;

          // Grab the slide and slider DOM elements
          const slideElement = editorView.nodeDOM(info.slidePos) as HTMLElement;
          if (!slideElement) {
            return;
          }
          const sliderElement = slideElement.parentElement;
          if (!sliderElement) {
            return;
          }

          // Dispatch a custom event to notify the slideshow node view component of the slide change
          sliderElement.dispatchEvent(new CustomEvent('selection-in-slide', {
            detail: { slideIndex: info.slideIndex },
            bubbles: true,
            composed: true,
            cancelable: false
          }));
        }
      };
    }
  });
}
